import { Suspense } from "react"
import { SetValueConfig, useFormContext, useWatch } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { FormError, FormField } from "@/components/ui/form"
import { LabelAndSelectValue, Select, SelectContent, SelectItem, SelectTrigger } from "@/components/ui/select"
import {
  ADDRESS_SOURCES,
  AddressSource,
  TeleconsultationForm,
} from "@/modules/patients/agenda/teleconsultation/booking/teleconsultation-schema"
import { CitySelector } from "@/modules/patients/components/city-selector"
import { CountrySelector } from "@/modules/patients/components/country-selector"
import { HouseNumberStreet } from "@/modules/patients/components/house-number-street"
import { PostalCodeSelector } from "@/modules/patients/components/postalcode-selector"

type AddressSectionProps = {
  city?: string
  countryIsoCode2?: string
  houseNumberStreet?: string
  postalCode?: string
  partnerAddress?: string | null
}

type AddressOption = {
  source: AddressSource
  label: string
  dataTestPrefix: string
}

const NO_INFORMATION = "N/A"

const triggerOptions: SetValueConfig = { shouldValidate: true, shouldDirty: true }

const AddressSection = ({
  city,
  countryIsoCode2,
  houseNumberStreet,
  postalCode,
  partnerAddress,
}: AddressSectionProps) => {
  const { t } = useTranslation()
  const {
    control,
    setValue,
    formState: { errors },
  } = useFormContext<TeleconsultationForm>()
  const addressSourceWatch = useWatch({ control, name: "addressSource" })
  const isPartnerRecipient = addressSourceWatch === "PARTNER"

  const homeAddressLabel = t("patient.agenda.address.homeAddress") + ":"
  const displayAddress =
    city || countryIsoCode2 || houseNumberStreet || postalCode
      ? `${homeAddressLabel} ${houseNumberStreet}, ${postalCode} ${city}, ${countryIsoCode2}`
      : homeAddressLabel

  const partnerAddressLabel = partnerAddress ? `${t("patient.agenda.address.partnerAddress")}: ${partnerAddress}` : ""

  const options: AddressOption[] = [
    { source: ADDRESS_SOURCES.HOME, label: displayAddress, dataTestPrefix: "home-address" },
    ...(partnerAddress
      ? [{ source: ADDRESS_SOURCES.PARTNER_LOCATION, label: partnerAddressLabel, dataTestPrefix: "partner-address" }]
      : []),
    { source: ADDRESS_SOURCES.MANUAL, label: t(`patient.agenda.address.manually`), dataTestPrefix: "manual-address" },
  ]

  const selectedOption = options.find((o) => o.source === addressSourceWatch)

  const handleCountryChange = (value: string) => {
    const latestValue = value !== countryIsoCode2 ? NO_INFORMATION : ""

    setValue("city", latestValue, triggerOptions)
    setValue("postalCode", latestValue, triggerOptions)
    setValue("houseNumberStreet", latestValue, triggerOptions)
  }

  return (
    <>
      <FormField
        control={control}
        name="addressSource"
        render={({ field: { onChange, name } }) => (
          <div id={name}>
            <Select
              value={addressSourceWatch ?? ""}
              disabled={isPartnerRecipient}
              onValueChange={(v) => {
                const option = options.find((o) => o.source === v)
                if (option) onChange(option.source)
              }}
              data-testid="address-select">
              <SelectTrigger data-testid="patient-address-select-trigger" hasError={!!errors.addressSource}>
                <LabelAndSelectValue
                  required
                  label={
                    isPartnerRecipient
                      ? t("patient.agenda.address.partnerAddress")
                      : t("patient.agenda.address.location")
                  }
                  value={isPartnerRecipient ? partnerAddressLabel : (selectedOption?.label ?? "")}
                />
              </SelectTrigger>
              <SelectContent className="max-w-(--radix-select-trigger-width)">
                {options.map((option) => (
                  <SelectItem
                    key={option.source}
                    value={option.source}
                    data-testid={`select-option-${option.dataTestPrefix}`}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.addressSource?.message && <FormError>{t(errors.addressSource.message)}</FormError>}
          </div>
        )}
      />

      {addressSourceWatch === ADDRESS_SOURCES.MANUAL && (
        <Suspense>
          <div className="grid w-full grid-cols-4 gap-4">
            <CountrySelector onCountryChange={handleCountryChange} />
            <PostalCodeSelector />
            <CitySelector required />
            <HouseNumberStreet />
          </div>
        </Suspense>
      )}
    </>
  )
}

export { AddressSection, NO_INFORMATION }
